// Copyright (c) Ping. All rights reserved.
//
// Flying-emoji reaction overlay. Each reaction floats up + drifts + fades over
// ~4s via a CSS keyframe (GPU-composited transform/opacity — no per-frame JS,
// no jank). Each one carries the sender's initials avatar + name so everyone
// sees who reacted. Web-only (apps/web). Reactions arrive in realtime over the
// LiveKit data channel; the store caps the list, so this never unbounds.

import { useEffect } from 'react';

import type { CallReactionFlashView } from '@calls/store/callStore';

const STYLE_ID = 'ping-call-reaction-fly';

function ensureKeyframes(): void {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = `
@keyframes ping-react-fly {
  0%   { transform: translateY(0) scale(0.6); opacity: 0; }
  12%  { transform: translateY(-12px) scale(1.15); opacity: 1; }
  30%  { transform: translateY(-60px) scale(1); opacity: 1; }
  100% { transform: translateY(-220px) scale(0.9); opacity: 0; }
}`;
  document.head.appendChild(el);
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

export function ReactionFly({ reactions }: { reactions: readonly CallReactionFlashView[] }): JSX.Element {
  useEffect(() => {
    ensureKeyframes();
  }, []);

  return (
    <div
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 96,
        height: 0,
        pointerEvents: 'none',
        zIndex: 5,
      }}
    >
      {reactions.map((r, i) => (
        <div
          key={r.id}
          style={{
            position: 'absolute',
            // Deterministic horizontal jitter from the id so two reactions
            // don't perfectly overlap (no Math.random in render).
            left: `calc(50% + ${(hashCode(r.id) % 160) - 80}px)`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 4,
            willChange: 'transform, opacity',
            animation: 'ping-react-fly 4s ease-out forwards',
            animationDelay: `${(i % 3) * 60}ms`,
          }}
        >
          <span style={{ fontSize: 34, lineHeight: '34px' }}>{r.emoji}</span>
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              padding: '2px 8px 2px 2px',
              borderRadius: 999,
              background: 'rgba(0,0,0,0.55)',
              border: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            <span
              style={{
                width: 18,
                height: 18,
                borderRadius: 999,
                background: '#537EFF',
                color: '#fff',
                fontSize: 9,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {initialsOf(r.fromAccount)}
            </span>
            <span
              style={{
                fontSize: 11,
                color: '#E8EAED',
                maxWidth: 96,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {r.fromAccount}
            </span>
          </span>
        </div>
      ))}
    </div>
  );
}

function hashCode(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}
